import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import {
  FlaskConical,
  Loader2,
  MessageCircle,
  Play,
} from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import {
  listGenerationGalleryOptions,
  listGenerationGalleryQueryKey,
  listVerticalPacksOptions,
  previewVerticalPacksMutation,
} from '@/api';
import type {
  GenerationGalleryGroup,
  GenerationGallerySession,
  VerticalPackListItem,
  VerticalPackPreviewRow,
} from '@/api/generated/types.gen';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import useTestSession from '@/hooks/useTestSession';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_auth/dev/generation')({
  component: GenerationLabPage,
});

const PACK_FILTERS = ['science', 'math', 'history', 'language', 'default'] as const;

function chipClass(active: boolean) {
  return cn(
    'rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
    active
      ? 'bg-neutral-900 text-white hover:bg-neutral-800'
      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200',
  );
}

function GenerationLabPage() {
  return (
    <div className="space-y-5 py-2">
      <div className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-[-0.02em] text-neutral-900">
          <FlaskConical className="h-5 w-5" />
          Generation lab
        </h1>
        <p className="text-sm text-neutral-500">
          Isolated vertical packs. Dry-run the matrix, then click through what you generated.
        </p>
      </div>

      <Tabs defaultValue="matrix">
        <TabsList className="rounded-full bg-neutral-100 p-1">
          <TabsTrigger value="matrix" className="rounded-full px-3">
            Pack matrix
          </TabsTrigger>
          <TabsTrigger value="gallery" className="rounded-full px-3">
            Gallery
          </TabsTrigger>
        </TabsList>
        <TabsContent value="matrix" className="mt-4">
          <PackMatrix />
        </TabsContent>
        <TabsContent value="gallery" className="mt-4">
          <GenerationGallery />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PackMatrix() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);
  const { data, isLoading } = useQuery({
    ...listVerticalPacksOptions(),
    refetchOnWindowFocus: false,
  });
  const preview = useMutation({
    ...previewVerticalPacksMutation(),
    onSuccess: (result) => {
      const liveCount = result.rows.filter((row) => row.group_id).length;
      if (liveCount) {
        toast.success(`${liveCount} path${liveCount === 1 ? '' : 's'} queued`);
        queryClient.invalidateQueries({ queryKey: listGenerationGalleryQueryKey() });
      }
    },
    onError: () => toast.error('Preview failed'),
  });

  const packs = data?.packs ?? [];
  const rowsById = new Map((preview.data?.rows ?? []).map((row) => [row.pack_id, row]));

  const togglePack = (id: string) => {
    setSelected((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  };

  const runPreview = (mode: 'dry' | 'plan', live = false) => {
    preview.mutate({
      body: {
        pack_ids: selected.length ? selected : null,
        mode,
        live,
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {packs.map((pack) => (
          <button
            key={pack.id}
            type="button"
            onClick={() => togglePack(pack.id)}
            className={chipClass(selected.length === 0 || selected.includes(pack.id))}
          >
            {pack.id}
          </button>
        ))}
        <span className="hidden sm:inline text-xs text-neutral-400">empty = all</span>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button size="sm" disabled={preview.isPending} onClick={() => runPreview('dry')}>
            {preview.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Dry-run
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={preview.isPending}
            onClick={() => runPreview('plan')}
          >
            Plan only
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={preview.isPending}
            onClick={() => runPreview('dry', true)}
          >
            Live
          </Button>
        </div>
      </div>
      <p className="text-xs text-neutral-400">
        Dry-run reads pack rules only. Plan calls Gemini for structure + visuals, no video tasks.
        Live queues a real path per selected pack.
      </p>

      {isLoading ? (
        <p className="text-sm text-neutral-500">Loading packs…</p>
      ) : (
        <div className="space-y-3">
          {packs.map((pack) => (
            <PackRow key={pack.id} pack={pack} row={rowsById.get(pack.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function PackRow({
  pack,
  row,
}: {
  pack: VerticalPackListItem;
  row?: VerticalPackPreviewRow;
}) {
  const video = row?.video ?? packWouldShowVideo(pack);
  return (
    <article className="rounded-2xl bg-neutral-50 p-4 ring-1 ring-black/5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-semibold text-neutral-900">{pack.id}</h2>
            <span className="rounded-full bg-white px-2 py-0.5 text-[11px] text-neutral-500 ring-1 ring-black/5">
              {pack.video_policy}
            </span>
            <span className="rounded-full bg-white px-2 py-0.5 text-[11px] text-neutral-500 ring-1 ring-black/5">
              {video ? 'video yes' : 'video no'}
            </span>
            <span className="rounded-full bg-white px-2 py-0.5 text-[11px] text-neutral-500 ring-1 ring-black/5">
              {pack.still_count} stills
            </span>
            <span className="rounded-full bg-white px-2 py-0.5 text-[11px] text-neutral-500 ring-1 ring-black/5">
              hands {pack.hands}
            </span>
            <span className="rounded-full bg-white px-2 py-0.5 text-[11px] text-neutral-500 ring-1 ring-black/5">
              labels {pack.labels}
            </span>
          </div>
          <p className="text-sm text-neutral-600">{pack.summary}</p>
          <p className="text-xs text-neutral-400">{pack.fixture_prompt}</p>
          <p className="text-xs text-neutral-500">{row?.addendum_excerpt ?? pack.addendum_excerpt}</p>
        </div>
        {row?.group_id ? (
          <Button size="sm" asChild>
            <Link to="/learn/path/$id" params={{ id: row.group_id }}>
              Open path
            </Link>
          </Button>
        ) : null}
      </div>
      {row?.path_name ? (
        <p className="mt-2 text-xs text-neutral-500">
          Plan: {row.path_name}
          {row.resolved_pack_id && row.resolved_pack_id !== pack.id
            ? ` · resolved ${row.resolved_pack_id}`
            : ''}
        </p>
      ) : null}
      {row?.sessions?.length ? (
        <ul className="mt-2 space-y-1">
          {row.sessions.map((session) => (
            <li key={session.title} className="text-xs text-neutral-600">
              {session.title}
              {session.video_recommended ? ' · video' : ''}
              {session.still_count ? ` · ${session.still_count} stills` : ''}
            </li>
          ))}
        </ul>
      ) : null}
      {row?.error ? <p className="mt-2 text-xs text-red-600">{row.error}</p> : null}
    </article>
  );
}

function packWouldShowVideo(pack: VerticalPackListItem) {
  return pack.video_policy === 'process' || pack.video_policy === 'transformation_only' || pack.video_policy === 'default';
}

function GenerationGallery() {
  const [pack, setPack] = useState<string | null>(null);
  const [hasVideo, setHasVideo] = useState<boolean | null>(null);
  const [mine, setMine] = useState(false);

  const { data, isLoading } = useQuery({
    ...listGenerationGalleryOptions({
      query: {
        pack,
        has_video: hasVideo,
        mine,
        limit: 40,
      },
    }),
    refetchOnWindowFocus: true,
  });

  const groups = data?.groups ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <button type="button" className={chipClass(pack === null)} onClick={() => setPack(null)}>
          All packs
        </button>
        {PACK_FILTERS.map((id) => (
          <button
            key={id}
            type="button"
            className={chipClass(pack === id)}
            onClick={() => setPack(id)}
          >
            {id}
          </button>
        ))}
        <button
          type="button"
          className={chipClass(hasVideo === true)}
          onClick={() => setHasVideo(hasVideo === true ? null : true)}
        >
          Has video
        </button>
        <button
          type="button"
          className={chipClass(mine)}
          onClick={() => setMine((value) => !value)}
        >
          Mine
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-neutral-500">Loading gallery…</p>
      ) : groups.length === 0 ? (
        <p className="text-sm text-neutral-500">No groups yet. Run a live pack test, then refresh.</p>
      ) : (
        <div className="space-y-3">
          {groups.map((group) => (
            <GalleryGroupCard key={group.id} group={group} />
          ))}
        </div>
      )}
    </div>
  );
}

function GalleryGroupCard({ group }: { group: GenerationGalleryGroup }) {
  const created = group.created_at
    ? formatDistanceToNow(new Date(group.created_at), { addSuffix: true })
    : null;
  const firstSession = group.sessions?.[0];

  return (
    <article className="overflow-hidden rounded-2xl bg-white ring-1 ring-black/5">
      <div className="flex gap-3 p-3">
        {group.cover_image_url ? (
          <img
            src={group.cover_image_url}
            alt=""
            className="h-16 w-28 shrink-0 rounded-xl object-cover"
          />
        ) : (
          <div className="h-16 w-28 shrink-0 rounded-xl bg-neutral-100" />
        )}
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <h2 className="truncate text-sm font-semibold text-neutral-900">{group.name}</h2>
            <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] text-neutral-500">
              {group.pack_id}
            </span>
            <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] text-neutral-500">
              {group.generation_status}
            </span>
            {group.has_video ? (
              <span className="rounded-full bg-neutral-900 px-2 py-0.5 text-[11px] text-white">
                video
              </span>
            ) : null}
          </div>
          <p className="text-xs text-neutral-500">
            {group.type} · {group.session_count} sessions
            {group.categories?.length ? ` · ${group.categories.join(', ')}` : ''}
            {created ? ` · ${created}` : ''}
          </p>
          <div className="flex flex-wrap gap-1.5 pt-1">
            <Button size="xs" asChild>
              <Link to="/learn/path/$id" params={{ id: group.id }}>
                Open path
              </Link>
            </Button>
            {firstSession ? <StartSessionButton templateId={firstSession.id} /> : null}
            <Button size="xs" variant="secondary" asChild>
              <Link to="/chat" search={{ q: group.name }}>
                <MessageCircle className="h-3 w-3" />
                Chat
              </Link>
            </Button>
          </div>
        </div>
      </div>
      {group.sessions?.length ? (
        <ul className="border-t border-black/5 px-3 py-2">
          {group.sessions.map((session) => (
            <GallerySessionRow key={session.id} session={session} />
          ))}
        </ul>
      ) : null}
    </article>
  );
}

function GallerySessionRow({ session }: { session: GenerationGallerySession }) {
  return (
    <li className="flex items-center justify-between gap-2 py-1">
      <p className="truncate text-xs text-neutral-600">
        {session.name}
        {session.has_video ? ' · video' : ''}
        {session.duration ? ` · ${session.duration}m` : ''}
      </p>
      <StartSessionButton templateId={session.id} />
    </li>
  );
}

function StartSessionButton({ templateId }: { templateId: string }) {
  const { startTest, isPending } = useTestSession(templateId);
  return (
    <Button size="xs" variant="secondary" onClick={startTest} disabled={isPending}>
      {isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3 fill-current" />}
      Start
    </Button>
  );
}
